import type { SessionStore, SessionUser } from "../api/index.js";
import { ApiError, toRecoveryAction, type RecoveryAction, type ToRecoveryOptions } from "../recovery/index.js";
import type { AuthApi } from "./auth_api.js";
import type { IdentityProvider, IdentityUser } from "./identity.js";
import { canResend, normalizeOtp, otpFailure, otpIssued } from "./otp_step.js";
import { isValidEmail } from "./validation.js";

/**
 * Sign-in step machine (CAP-3, CAP-5). Parity with the Flutter app:
 *
 *   credentials ── Firebase password ──▶ unverified email? ──▶ unverified (Resend → otp/verify)
 *        │                 └──▶ POST /otp ──▶ otp (login) ── POST /loginWithOtp ──▶ done
 *        ├─ Google / Apple ── POST /login ──▶ done
 *        ├─ user-not-found / NoUserDoc ──▶ notFound (sign-up CTA; a Google/Apple
 *        │                                   identity stays signed in and is handed to sign-up)
 *        └─ account-exists-with-different-credential ──▶ accountExists
 *
 * Headless: no DOM, no React. Screens subscribe and call the actions.
 */

export type SignInStep = "credentials" | "otp" | "unverified" | "notFound" | "accountExists" | "done";

export type SignInField = "email" | "password" | "otp";

export interface SignInState {
  step: SignInStep;
  busy: boolean;
  email: string;
  otpId?: string;
  /** login: POST /loginWithOtp. verify: unverified email → POST /signupWithOtp. */
  otpMode: "login" | "verify";
  /** Epoch ms after which "Resend code" is available (30 s). */
  resendAvailableAt?: number;
  error?: RecoveryAction;
  /** i18n keys for per-field validation messages. */
  fieldErrors: Partial<Record<SignInField, string>>;
  /** Bumped when the OTP field must be cleared (wrong code). */
  otpResetCount: number;
  user?: SessionUser;
  /**
   * Google/Apple identity with no KoverNow account (notFound). It stays
   * signed in to Firebase (memory only) so sign-up can finish without a
   * second popup; `abandonSocial()` / `back()` sign it out.
   */
  social?: SocialHandoff;
}

/** What sign-up needs from a Google/Apple identity that has no account yet. */
export interface SocialHandoff {
  uid: string;
  email: string;
  displayName?: string;
  providerId?: string;
}

export interface SignInDeps {
  identity: IdentityProvider;
  api: AuthApi;
  session: SessionStore;
  now?: () => number;
  recovery?: ToRecoveryOptions;
  /** Prefill, e.g. from sign-up's "You already have an account". */
  initialEmail?: string;
}

export interface SignInFlow {
  getState(): SignInState;
  subscribe(cb: () => void): () => void;
  submitCredentials(email: string, password: string): Promise<void>;
  validateField(field: "email" | "password", value: string): void;
  signInWithGoogle(): Promise<void>;
  signInWithApple(): Promise<void>;
  /** After a full-page redirect fallback for a blocked popup. */
  resumeRedirect(): Promise<void>;
  resendVerification(): Promise<void>;
  submitOtp(code: string): Promise<void>;
  resendOtp(): Promise<void>;
  back(): void;
  dismissError(): void;
  /** Sign the handed-off Google/Apple identity out (sign-up abandoned). */
  abandonSocial(): Promise<void>;
}

const initialState = (email = ""): SignInState => ({
  step: "credentials",
  busy: false,
  email,
  otpMode: "login",
  fieldErrors: {},
  otpResetCount: 0,
});

function isNoUserDoc(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.problem?.detail === "NoUserDoc";
}

function firebaseCode(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code : undefined;
}

const NOT_FOUND_CODES = new Set(["auth/user-not-found"]);

export function createSignInFlow(deps: SignInDeps): SignInFlow {
  const now = deps.now ?? Date.now;
  let state = initialState(deps.initialEmail?.trim().toLowerCase() ?? "");
  const subs = new Set<() => void>();

  const set = (patch: Partial<SignInState>) => {
    state = { ...state, ...patch };
    for (const s of subs) s();
  };

  const fail = (err: unknown, extra: Partial<SignInState> = {}) => {
    set({ busy: false, error: toRecoveryAction(err, deps.recovery), ...extra });
  };

  const finish = (token: string, user: SessionUser) => {
    deps.session.setSession(token, user);
    set({ busy: false, step: "done", user, error: undefined, fieldErrors: {} });
  };

  const guardBusy = () => {
    if (state.busy) return true;
    set({ busy: true, error: undefined });
    return false;
  };

  /** Google/Apple (or redirect result) → backend /login. */
  const completeSocial = async (user: IdentityUser | null) => {
    if (!user) {
      // Closed popup / cancelled: a silent no-op.
      set({ busy: false });
      return;
    }
    try {
      const idToken = await user.getIdToken();
      const res = await deps.api.login(idToken);
      finish(res.token, res.user);
    } catch (err) {
      if (isNoUserDoc(err) && user.email) {
        // Keep the identity: sign-up finishes with it (spec: social sign-up).
        set({
          busy: false,
          step: "notFound",
          email: user.email,
          social: {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName ?? undefined,
            providerId: user.providerId,
          },
        });
        return;
      }
      if (isNoUserDoc(err)) {
        await deps.identity.signOut().catch(() => undefined);
        set({ busy: false, step: "notFound", email: state.email });
        return;
      }
      fail(err);
    }
  };

  const social = async (provider: () => Promise<IdentityUser | null>) => {
    if (guardBusy()) return;
    let user: IdentityUser | null;
    try {
      user = await provider();
    } catch (err) {
      if (firebaseCode(err) === "auth/account-exists-with-different-credential") {
        await deps.identity.signOut().catch(() => undefined);
        set({ busy: false, step: "accountExists" });
        return;
      }
      fail(err);
      return;
    }
    await completeSocial(user);
  };

  const validateEmail = (email: string) =>
    isValidEmail(email) ? undefined : "auth.validation.emailInvalid";
  const validatePassword = (pw: string) => (pw ? undefined : "auth.validation.passwordRequired");

  return {
    getState: () => state,
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },

    validateField(field, value) {
      const message = field === "email" ? validateEmail(value.trim()) : validatePassword(value);
      set({ fieldErrors: { ...state.fieldErrors, [field]: message } });
    },

    async submitCredentials(rawEmail, password) {
      const email = rawEmail.trim().toLowerCase();
      const fieldErrors = { email: validateEmail(email), password: validatePassword(password) };
      if (fieldErrors.email || fieldErrors.password) {
        set({ fieldErrors, email });
        return;
      }
      if (guardBusy()) return;
      set({ email, fieldErrors: {} });
      let user: IdentityUser;
      try {
        user = await deps.identity.signInWithPassword(email, password);
      } catch (err) {
        if (NOT_FOUND_CODES.has(firebaseCode(err) ?? "")) {
          set({ busy: false, step: "notFound" });
          return;
        }
        fail(err);
        return;
      }
      if (!user.emailVerified) {
        set({ busy: false, step: "unverified" });
        return;
      }
      try {
        const idToken = await user.getIdToken();
        const { otpId } = await deps.api.requestOtp(idToken);
        set({ busy: false, step: "otp", otpMode: "login", ...otpIssued(otpId, now()) });
      } catch (err) {
        if (isNoUserDoc(err)) {
          set({ busy: false, step: "notFound" });
          return;
        }
        fail(err);
      }
    },

    signInWithGoogle: () => social(() => deps.identity.signInWithGoogle()),
    signInWithApple: () => social(() => deps.identity.signInWithApple()),

    async resumeRedirect() {
      let user: IdentityUser | null;
      try {
        user = await deps.identity.completeRedirect();
      } catch (err) {
        if (firebaseCode(err) === "auth/account-exists-with-different-credential") {
          await deps.identity.signOut().catch(() => undefined);
          set({ step: "accountExists" });
          return;
        }
        fail(err);
        return;
      }
      if (!user) return;
      set({ busy: true });
      await completeSocial(user);
    },

    async resendVerification() {
      if (guardBusy()) return;
      try {
        const { otpId } = await deps.api.sendVerificationOtpEmail(state.email);
        set({ busy: false, step: "otp", otpMode: "verify", ...otpIssued(otpId, now()) });
      } catch (err) {
        fail(err);
      }
    },

    async submitOtp(code) {
      const otp = normalizeOtp(code);
      if (!otp) {
        set({ fieldErrors: { otp: "auth.validation.otpLength" } });
        return;
      }
      if (!state.otpId || guardBusy()) return;
      set({ fieldErrors: {} });
      try {
        const res =
          state.otpMode === "verify"
            ? await deps.api.signupWithOtp(state.otpId, otp)
            : await deps.api.loginWithOtp(state.otpId, otp);
        finish(res.token, res.user);
      } catch (err) {
        set(otpFailure(err, state, deps.recovery));
      }
    },

    async resendOtp() {
      // A dead code (locked/expired/used) can be replaced straight away.
      const dead = state.error?.action === "resend_code";
      if (!state.otpId || (!dead && !canResend(state, now())) || guardBusy()) return;
      try {
        const { otpId } = await deps.api.resendOtp(state.otpId);
        set({
          busy: false,
          error: undefined,
          ...otpIssued(otpId, now()),
          otpResetCount: state.otpResetCount + 1,
        });
      } catch (err) {
        fail(err);
      }
    },

    back() {
      if (state.social) void deps.identity.signOut().catch(() => undefined);
      // Replace (not merge) so otpId / user / resend timer are dropped.
      state = initialState(state.email);
      for (const s of subs) s();
    },

    dismissError() {
      set({ error: undefined });
    },

    async abandonSocial() {
      if (!state.social) return;
      set({ social: undefined });
      await deps.identity.signOut().catch(() => undefined);
    },
  };
}
